import { useIsFocused, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import ImageCarousel from "components/ImageCarousel";
import InnerLoading from "components/InnerLoading";
import { getWidth } from "components/MainContainer";
import Spacer from "components/Spacer";
import {
  setPremiumInventory as setReservationFlowPremiumInventory,
  setSelectedPremiumCode,
} from "features/slices/reservationFlowSlice";
import { PremiumInventoryDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { ScheduleStackParamList } from "navigation/types";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { useFetchPremiumInventoryQuery } from "queries/ticket.queries";
import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { getRemainingSlotsForPlanPackage } from "screens/Reserve/helpers";
import { formatCurrency } from "utils/appUtils";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import { getMaxSeatsAllowedPerReservation } from "utils/tenantUtils";
import toast from "utils/toast";
import PremiumCodeSelectorModal from "./components/PremiumCodeSelectorModal";
import { getPremiumImageUrls } from "./helpers";

const PremiumSeatSelectionFlow = () => {
  // navigation
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "SeatSelection">
    >();

  // redux
  const dispatch = useAppDispatch();
  const { selectedEvent, selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow,
  );

  // react-query
  const { data: premiumInventories, isFetching: isFetchingPremiumInventories } =
    useFetchPremiumInventoryQuery({
      planPackageId: selectedSubscription?.planPackage.id,
      eventId: selectedEvent?.eventId,
    });

  // local states
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPremiumInventory, setSelectedPremiumInventory] =
    useState<PremiumInventoryDetails>();
  const [hasRemainingSlots, setHasRemainingSlots] = useState(true);

  const isFocused = useIsFocused();

  // hooks
  const { isPremiumInventoryMarketingAssets } = useMarketingAssetsSections(
    selectedSubscription?.planPackage.id,
  );
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();
  const maxSeatsAllowed = getMaxSeatsAllowedPerReservation(tenantConfig);
  const { account, accountEvents: events } = useAccount();

  const matchedSubs = useMemo(() => {
    if (!account || !selectedSubscription) return [];
    return account.subscriptions.filter(
      (sub) =>
        filterIneligibleSubscriptions(sub) &&
        sub.planPackage.id === selectedSubscription.planPackage.id,
    );
  }, [account, selectedSubscription]);

  useEffect(() => {
    if (
      !isFocused ||
      !selectedEvent ||
      !tenantConfig ||
      matchedSubs.length === 0
    )
      return;
    const { selectedPlanType, partySize } = getRemainingSlotsForPlanPackage({
      subscriptions: matchedSubs,
      planPackage: selectedSubscription!.planPackage,
      eventId: selectedEvent.eventId,
      events,
      isChangingSeats: false,
      maxSeatsAllowed,
    });
    if (partySize <= 0) {
      setHasRemainingSlots(false);
      const noReservation = i18n.t("screens.SeatSelection.noReservation");
      const noVoucher = i18n.t("screens.SeatSelection.noVoucher");
      const generateMsg = (msg: string) =>
        i18n.t("screens.SeatSelection.noTicketMsg", { msg });
      let msg = "";
      switch (selectedPlanType) {
        case "VOUCHER":
          msg = generateMsg(noVoucher);
          break;
        case "BANK":
        case "RESERVATION":
          msg = generateMsg(noReservation);
          break;
      }
      toast.warning(msg);
    }
  }, [isFocused, selectedEvent, matchedSubs, tenantConfig]);

  const handleViewDetails = (premiumInventory: PremiumInventoryDetails) => {
    if (!selectedEvent) return;
    if (premiumInventory.minPartySize === 0) return;
    setSelectedPremiumInventory(premiumInventory);

    if (selectedEvent.eventType === "EXPERIENCE") {
      dispatch(setReservationFlowPremiumInventory(premiumInventory));
      dispatch(setSelectedPremiumCode(premiumInventory.premiumCodes[0]));
      navigation.navigate("PremiumCodeDetails");
      return;
    }
    setModalOpen(true);
  };

  const filteredInventories = useMemo(() => {
    if (!premiumInventories || !selectedEvent) return [];

    // According to convention, if the event type is experience,
    // only show premium inventories that have the same premium class name as the event name
    // so premium class like private venue tour won't show up under a meet and greet event.
    if (selectedEvent.eventType === "EXPERIENCE")
      return premiumInventories.filter(
        (inventory) => inventory.premiumClass.name === selectedEvent?.eventName,
      );
    const sortedByAvailablity = premiumInventories.sort((a, b) => {
      if (a.minPartySize === 0) return 1;
      if (b.minPartySize === 0) return -1;
      return 0;
    });

    return sortedByAvailablity;
  }, [premiumInventories]);

  // Skip this page for RSVP events, go directly to PremiumCodeDetails
  useEffect(() => {
    if (!premiumInventories) return;

    if (selectedEvent?.eventType === "RSVP") {
      const premiumInventory = premiumInventories[0];
      dispatch(setReservationFlowPremiumInventory(premiumInventory));
      dispatch(setSelectedPremiumCode(premiumInventory.premiumCodes[0]));
      navigation.navigate("PremiumCodeDetails");
    }
  }, [premiumInventories, selectedEvent]);

  if (!selectedEvent || !selectedSubscription) return null;
  if (!premiumInventories) return <InnerLoading />;

  const isMarketingAssetsOrExperienceEvent = (
    inventory: PremiumInventoryDetails,
  ) => {
    return (
      isPremiumInventoryMarketingAssets(inventory) ||
      selectedEvent.eventType === "EXPERIENCE"
    );
  };

  // Do not show estimated total for NIL events
  // https://github.com/FanRally/fanrally-core/issues/1485
  const shouldShowTotal = !["NIL", "RSVP"].includes(
    selectedEvent?.eventType || "",
  );

  return (
    <ScrollView
      contentContainerStyle={{
        padding: 4,
        flex: 1,
        maxWidth: getWidth(),
        minWidth: getWidth(),
        alignSelf: "center",
      }}
    >
      {/* {hasMarketingAssets && (
        <CheckBox
          style={{ alignSelf: "flex-end", marginVertical: 10 }}
          checked={showMarketingAssets}
          onChange={handleToggleMarketingAssetsInventory}
        >
          Hospitality and Marketing
        </CheckBox>
      )} */}
      {filteredInventories.map((inventory) => {
        const {
          minPartySize,
          priceType,
          premiumClass: { name, longDescription, type },
          premiumCodes,
          minPlanTicketFees,
        } = inventory;
        const minPrice =
          Math.min(
            ...premiumCodes
              .filter((code) => code.minPartySize <= code.maxPartySize) // filter our unavailable premium codes
              .map((code) => Number(code.minBundlePrice)),
          ) + Number(minPlanTicketFees);
        return (
          <View style={styles.card} key={inventory.premiumClass.id}>
            <View>
              <ImageCarousel
                sources={getPremiumImageUrls(
                  inventory.premiumClass,
                  selectedEvent!,
                )}
                imageHeight="150px"
              />
            </View>

            <View style={styles.cardBody}>
              <View>
                <Text category="h6">{name}</Text>
              </View>
              {!shouldShowTotal && (
                <Text category="h6">
                  {formatCurrency(minPrice, { priceType })}{" "}
                  {type === "SEATS" ? "per person" : ""}
                </Text>
              )}
              <Spacer margin={5} />

              <Text category="c1">{longDescription}</Text>

              <Spacer margin={2} />

              <Button
                style={{ flex: 0.3, marginTop: 8 }}
                onPress={() => handleViewDetails(inventory)}
                disabled={isNaN(minPrice) || !hasRemainingSlots}
              >
                {minPartySize === 0
                  ? "Sold Out"
                  : isMarketingAssetsOrExperienceEvent(inventory)
                    ? "Continue"
                    : "Show Available Locations"}
              </Button>
            </View>
          </View>
        );
      })}
      {selectedPremiumInventory && (
        <PremiumCodeSelectorModal
          premiumInventory={selectedPremiumInventory}
          isMarketingAssets={isPremiumInventoryMarketingAssets(
            selectedPremiumInventory,
          )}
          visible={modalOpen}
          onDismiss={() => setModalOpen(false)}
        />
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  card: {
    marginBottom: 15,
    borderWidth: 1,
    borderColor: "#e4e9f2",
    borderRadius: 8,
  },
  cardBody: {
    justifyContent: "space-between",
    padding: 8,
  },
  alignCenter: {
    flexDirection: "row",
    alignItems: "center",
  },
});
export default PremiumSeatSelectionFlow;
